import { useEffect, useState, useMemo, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useToast } from '../components/context/ToastContext';
import { useBillRealtime, useUnreadCounts } from '../hooks/useBillRealtime';
import BillRow from '../components/BillRow';
import BillDetailModal from '../components/BillDetailModal';
import type { DetailTab } from '../components/BillDetailModal';
import Modal from '../components/Modal';
import ConfirmDialog from '../components/ConfirmDialog';
import { FolderOpen, Plus, Search, SearchX } from 'lucide-react';
import type { Bill, BillStatus, BillUpdate, BillCommunicationLog } from '../types/bill';
import { loadBillDrafts, type BillDraft } from '../lib/freeAccount';
import '@styles/pages/dashboard.css';
import '@styles/pages/cases.css';
import '@styles/pages/home.css';

/* v3 simplification: status chips → 3 tabs.
   - Active groups everything mid-flight (submitted, in_review, negotiating,
     negotiating_more, financial_assistance, info_needed).
   - Closed groups final states (completed, no_savings, canceled). */
type FilterValue = 'all' | 'active' | 'closed';

const CLOSED_STATUSES: BillStatus[] = ['completed', 'no_savings', 'canceled'];

const FILTER_TABS: { value: FilterValue; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'active', label: 'Active' },
  { value: 'closed', label: 'Closed' },
];

/* ==================================
   BILLS PAGE
================================== */
export default function Bills() {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { user, loading: authLoading } = useAuth();

  const [bills, setBills] = useState<Bill[]>([]);
  const [loading, setLoading] = useState(true);
  const [drafts, setDrafts] = useState<BillDraft[]>([]);

  const [selectedBill, setSelectedBill] = useState<Bill | null>(null);
  const [detailTab, setDetailTab] = useState<DetailTab>('progress');

  /* Updates & Activity for the selected bill */
  const [billUpdates, setBillUpdates] = useState<BillUpdate[]>([]);
  const [commLogs, setCommLogs] = useState<BillCommunicationLog[]>([]);
  // loadingDetail used to flip while loadBillDetails ran; modal path is
  // retired (v3 Phase 4r2 sends clicks to /case/:id), so it's now a const.
  const [loadingDetail] = useState(false);

  const [editingBill, setEditingBill] = useState<Bill | null>(null);
  const [editName, setEditName] = useState('');
  const [editComment, setEditComment] = useState('');
  const [editSaving, setEditSaving] = useState(false);

  const [deletingBill, setDeletingBill] = useState<Bill | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [subscription, setSubscription] = useState<{ bills_used: number; bills_limit: number } | null>(null);

  /* Search & Filter */
  const [searchQuery, setSearchQuery] = useState('');
  const [filterValue, setFilterValue] = useState<FilterValue>('all');

  /* Unread counts */
  const billIds = useMemo(() => bills.map((b) => b.id), [bills]);
  const { unreadCounts, markAsRead, refetchCounts } = useUnreadCounts(billIds);

  /* Realtime subscriptions for the currently-open bill */
  const handleRealtimeUpdate = useCallback((update: BillUpdate) => {
    setBillUpdates((prev) => [update, ...prev]);
  }, []);

  const handleRealtimeCommLog = useCallback((log: BillCommunicationLog) => {
    setCommLogs((prev) => [log, ...prev]);
  }, []);

  useBillRealtime({
    billId: selectedBill?.id ?? null,
    onNewUpdate: handleRealtimeUpdate,
    onNewCommLog: handleRealtimeCommLog,
  });

  const filteredBills = useMemo(() => {
    let result = bills;

    if (filterValue === 'active') {
      result = result.filter((b) => !CLOSED_STATUSES.includes(b.status));
    } else if (filterValue === 'closed') {
      result = result.filter((b) => CLOSED_STATUSES.includes(b.status));
    }

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(
        (b) =>
          (b.display_name || '').toLowerCase().includes(q) ||
          (b.provider_name || '').toLowerCase().includes(q) ||
          b.file_path.toLowerCase().includes(q)
      );
    }

    return result;
  }, [bills, filterValue, searchQuery]);

  /* Per-tab counts for the segmented control */
  const filterCounts = useMemo(() => {
    const closed = bills.filter((b) => CLOSED_STATUSES.includes(b.status)).length;
    return {
      all: bills.length,
      active: bills.length - closed,
      closed,
    };
  }, [bills]);

  /* Portfolio stats for the v3 header strip, computed from the in-memory
     bills array so no extra DB roundtrip. Active = anything not in a
     terminal state (completed, no_savings, canceled). */
  const portfolioStats = useMemo(() => {
    const active = bills.filter(
      (b) => !CLOSED_STATUSES.includes(b.status),
    ).length;
    const closed = bills.filter((b) => b.status === 'completed').length;
    const totalSaved = bills
      .filter((b) => b.status === 'completed')
      .reduce((acc, b) => acc + (Number(b.amount_saved) || 0), 0);
    return { active, closed, totalSaved };
  }, [bills]);

  useEffect(() => {
    if (authLoading) return;

    if (!user) {
      navigate('/login');
      return;
    }

    const load = async () => {
      const { data, error } = await supabase
        .from('bills')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) {
        showToast('Failed to load your bills', 'error');
      } else if (data) {
        setBills(data);
      }

      const { data: subData } = await supabase
        .from('subscriptions')
        .select('bills_used, bills_limit')
        .eq('user_id', user.id)
        .maybeSingle();

      if (subData) setSubscription(subData);
      /* A free account's uploaded bill isn't a bills row yet. Without this
         the Cases page said "No cases yet" while Home showed the bill.
         There can be several since migration 115, each one is listed. */
      setDrafts(await loadBillDrafts(user.id));
      setLoading(false);
    };

    load();
  }, [authLoading, user, navigate, showToast]);

  /* v3: clicking a bill now opens the full /case/:id workspace (Phase 4r2),
     not the modal. Modal is still reachable via BillCard's "Quick view"
     and from elsewhere, kept around for the migration window so any deep
     link that opens it still works. Mark-as-read happens on navigation so
     the unread badge clears the same way the modal did. */
  const handleBillSelect = useCallback((bill: Bill) => {
    markAsRead(bill.id);
    navigate(`/case/${bill.id}`);
  }, [markAsRead, navigate]);

  const handleModalClose = useCallback(() => {
    if (selectedBill) {
      markAsRead(selectedBill.id);
    }
    setSelectedBill(null);
    // Refresh unread counts after closing (in case realtime pushed new updates)
    refetchCounts();
  }, [selectedBill, markAsRead, refetchCounts]);

  /* Edit */
  const handleEditClick = (bill: Bill) => {
    setEditingBill(bill);
    setEditName(bill.display_name || bill.file_path.split('/').pop() || '');
    setEditComment(bill.comment || '');
  };

  const handleEditSave = async () => {
    if (!editingBill || !editName.trim()) return;
    setEditSaving(true);
    const { error } = await supabase
      .from('bills')
      .update({ display_name: editName.trim(), comment: editComment.trim() || null })
      .eq('id', editingBill.id);
    setEditSaving(false);
    if (error) {
      showToast('Failed to update bill name', 'error');
      return;
    }
    setBills((prev) =>
      prev.map((b) =>
        b.id === editingBill.id ? { ...b, display_name: editName.trim(), comment: editComment.trim() || null } : b
      )
    );
    showToast('Bill name updated!');
    setEditingBill(null);
  };

  /* Delete */
  const handleDeleteConfirm = async () => {
    if (!deletingBill || !user) return;
    setDeleteLoading(true);

    await supabase.storage.from('medical_bills').remove([deletingBill.file_path]);

    // Use a SECURITY DEFINER RPC instead of a direct UPDATE.
    // Root cause: the bills_select RLS policy has `deleted_at IS NULL`, so
    // after a raw UPDATE sets deleted_at, PostgreSQL's RLS engine checks
    // whether the NEW row is still visible through SELECT, it isn't, and
    // raises code 42501.  The function bypasses that check while still
    // enforcing ownership inside its body.
    const { error } = await supabase.rpc('soft_delete_bill', { bill_id: deletingBill.id });
    setDeleteLoading(false);
    if (error) {
      console.error('[Bills] delete error:', error);
      showToast('Failed to delete bill', 'error');
      setDeletingBill(null);
      return;
    }
    setBills((prev) => prev.filter((b) => b.id !== deletingBill.id));

    // Decrement bills_used in subscription
    try {
      const { error: rpcError } = await supabase.rpc('decrement_bills_used', { uid: user.id });
      if (rpcError) {
        await supabase
          .from('subscriptions')
          .update({ bills_used: Math.max(0, (subscription?.bills_used ?? 1) - 1) })
          .eq('user_id', user.id);
      }
      setSubscription((prev) =>
        prev ? { ...prev, bills_used: Math.max(0, prev.bills_used - 1) } : prev
      );
    } catch {
      showToast('Bill deleted, but usage count may be off', 'error');
    }

    showToast('Bill deleted');
    setDeletingBill(null);
  };

  return (
    <>
      {/* Page title row */}
      <div className="cases-page-header">
        <div className="cases-header-top">
          <div>
            <h1 className="cases-title ml-title">Cases</h1>
            {!loading && bills.length > 0 && (
              <p className="cases-subline">
                <span className="num">{portfolioStats.active}</span> active ·{' '}
                <span className="num">{portfolioStats.closed}</span> closed
                {portfolioStats.totalSaved > 0 && (
                  <>
                    {' '}·{' '}
                    <span className="cases-subline-saved num">
                      ${portfolioStats.totalSaved.toLocaleString()} saved
                    </span>
                  </>
                )}
              </p>
            )}
          </div>
          {!loading && bills.length > 0 && (
            <button className="cases-new-btn ml-btn ml-btn--ink" onClick={() => navigate('/add-bill')}>
              <Plus size={16} /> New case
            </button>
          )}
        </div>


        {/* Unified toolbar — search and filter sit in one bar, no gap */}
        {!loading && bills.length > 0 && (
          <div className="cases-toolbar cases-toolbar--unified">
            <div className="cases-search">
              <Search size={15} className="cases-search-icon" />
              <input
                type="text"
                placeholder="Search by provider or name…"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="cases-search-input"
              />
            </div>
            <div className="cases-toolbar-divider" aria-hidden />
            <div className="cases-segmented" role="tablist" aria-label="Filter cases">
              {FILTER_TABS.map((tab) => (
                <button
                  key={tab.value}
                  role="tab"
                  aria-selected={filterValue === tab.value}
                  className={`cases-segmented-btn ${filterValue === tab.value ? 'active' : ''}`}
                  onClick={() => setFilterValue(tab.value)}
                >
                  {tab.label}
                  <span className="cases-segmented-count">
                    {filterCounts[tab.value]}
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

        {/* Cases list */}
        {loading ? (
          <div className="bills-list" aria-busy>
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="bill-row bill-row-skeleton" aria-hidden>
                <div className="skeleton skeleton-dot" />
                <div className="bill-row-skel-main">
                  <div className="skeleton skeleton-text-md" />
                  <div className="skeleton skeleton-text-sm" style={{ marginTop: 6, width: '40%' }} />
                </div>
                <div className="skeleton skeleton-badge" />
              </div>
            ))}
          </div>
        ) : bills.length === 0 && drafts.length > 0 ? (
          <div className="bills-list" role="list">
            {drafts.map((draft) => (
              <button
                key={draft.id}
                type="button"
                className="ml-card hm-case cases-draft"
                role="listitem"
                onClick={() => navigate(`/case/draft?id=${draft.id}`)}
              >
                <span className="hm-case-main">
                  <span className="hm-case-title">{draft.provider_name || draft.file_name || 'Your bill'}</span>
                  <span className="hm-case-line">Uploaded, waiting for you to start</span>
                </span>
                <span className="ml-pill ml-pill--draft">Not started</span>
              </button>
            ))}
          </div>
        ) : bills.length === 0 ? (
          <div className="empty-card ml-empty">
            <span className="ml-empty-icon"><FolderOpen size={28} strokeWidth={1.5} /></span>
            <p className="ml-empty-title">No cases yet</p>
            <p className="ml-empty-body">Upload a bill and we'll show you what we find.</p>
            <button className="upload-btn ml-btn ml-btn--ink" onClick={() => navigate('/add-bill')}>
              <Plus size={16} /> Upload a bill
            </button>
          </div>
        ) : filteredBills.length === 0 ? (
          <div className="empty-card ml-empty">
            <span className="ml-empty-icon"><SearchX size={28} strokeWidth={1.5} /></span>
            <p className="ml-empty-title">Nothing matches that</p>
            <p className="ml-empty-body">
              Try a different search or filter, or clear them and start over.
            </p>
            <button
              className="upload-btn ml-btn ml-btn--ghost"
              onClick={() => {
                setSearchQuery('');
                setFilterValue('all');
              }}
            >
              Clear filters
            </button>
          </div>
        ) : (
          <div className="bills-list" role="list">
            {filteredBills.map((bill) => (
              <BillRow
                key={bill.id}
                bill={bill}
                onClick={() => handleBillSelect(bill)}
                onEdit={() => handleEditClick(bill)}
                onDelete={() => setDeletingBill(bill)}
                unreadCount={unreadCounts[bill.id] || 0}
              />
            ))}
          </div>
        )}

      {/* Detail Modal */}
      {selectedBill && (
        <BillDetailModal
          bill={selectedBill}
          tab={detailTab}
          onTabChange={setDetailTab}
          onClose={handleModalClose}
          updates={billUpdates}
          commLogs={commLogs}
          loadingData={loadingDetail}
          hasUnread={(unreadCounts[selectedBill.id] || 0) > 0}
        />
      )}

      {/* Edit Modal */}
      <Modal open={!!editingBill} onClose={() => setEditingBill(null)} className="edit-modal">
        <h2>Edit Bill Details</h2>
        <label className="edit-modal-label">Bill name</label>
        <input type="text" value={editName} onChange={(e) => setEditName(e.target.value)} placeholder="Bill name" autoFocus />
        <label className="edit-modal-label">Comment</label>
        <textarea
          value={editComment}
          onChange={(e) => setEditComment(e.target.value)}
          placeholder="Anything we should know? (optional)"
          rows={3}
          className="edit-modal-textarea"
        />
        <div className="edit-modal-actions">
          <button className="cancel" onClick={() => setEditingBill(null)} disabled={editSaving}>Cancel</button>
          <button className="save" onClick={handleEditSave} disabled={editSaving || !editName.trim()}>
            {editSaving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </Modal>

      <ConfirmDialog
        open={!!deletingBill}
        title="Delete bill?"
        message={`Are you sure you want to delete "${deletingBill?.display_name || deletingBill?.file_path.split('/').pop()}"?`}
        confirmLabel={deleteLoading ? 'Deleting...' : 'Delete'}
        cancelLabel="Cancel"
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeletingBill(null)}
        variant="danger"
      />
    </>
  );
}
